import { PlayCircleIcon, TrendingUpIcon, UsersIcon, LucideIcon } from "lucide-react"

interface Feature {
  /**
   * Feature title
   */
  title: string;

  /**
   * Feature description
   */
  description: string;

  /**
   * Icon component
   */
  icon: LucideIcon;
}

interface FeaturesSectionProps {
  /**
   * Section title
   */
  title?: string;

  /**
   * Section description
   */
  description?: string;

  /**
   * Array of features to display
   */
  features?: Feature[];

  /**
   * Additional CSS classes
   */
  className?: string;
}

const defaultFeatures: Feature[] = [
  {
    title: "Structured Learning",
    description: "Video modules with quizzes to help you master customer discovery at your own pace",
    icon: PlayCircleIcon
  },
  {
    title: "Track Your Progress",
    description: "Validation data tracking to measure your progress and stay accountable",
    icon: TrendingUpIcon
  },
  {
    title: "Expert Coaching",
    description: "1-on-1 feedback sessions with experienced founders who've built successful startups",
    icon: UsersIcon
  }
]

export function FeaturesSection({
  title = "Everything You Need to Validate Your Idea",
  description = "Join a structured program designed specifically for startup founders",
  features = defaultFeatures,
  className = ""
}: FeaturesSectionProps) {
  return (
    <section className={`bg-muted/30 py-16 md:py-24 ${className}`}>
      <div className="container mx-auto px-4 md:px-6">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            {title}
          </h2>
          <p className="text-lg text-muted-foreground">
            {description}
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
          {features.map((feature, index) => {
            const Icon = feature.icon
            return (
              <div
                key={index}
                className="bg-card border border-border rounded-lg p-8 hover:shadow-lg transition-shadow"
              >
                {/* Icon */}
                <div className="w-14 h-14 rounded-lg bg-primary/10 flex items-center justify-center mb-6">
                  <Icon className="w-7 h-7 text-primary" />
                </div>

                {/* Content */}
                <h3 className="text-xl font-semibold text-foreground mb-3">
                  {feature.title}
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  {feature.description}
                </p>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
